import { css } from '@emotion/react'
import {
  Button,
  Cell,
  DropdownItem,
  Grid,
  Heading,
  HFlow,
  Icon,
  Tag,
  Text,
  Theme,
  Tooltip,
  useTheme,
  VFlow,
} from 'bold-ui'
import { FieldStatusEnum, Form, FormRenderProps, ListFieldStatus, PecFormApi } from 'components/form'
import { HLabel } from 'components/HLabel'
import { TextStatus } from 'components/text/TextStatus'
import { useState } from 'react'
import { noop } from 'util/functions'
import { metaPath } from 'util/metaPath'
import { createValidator, maxLength } from 'util/validation'
import { getEditableItemRemoveTooltipText } from 'view/atendimentos/atendimento-individual/atendimento-observacao/util-atendObservacao'
import { EditableRow } from 'view/atendimentos/detail/components/EditableList'
import { Observacao } from 'view/atendimentos/detail/components/Observacao'
import { ObservacaoFormGrid } from 'view/atendimentos/detail/components/ObservacaoFormGrid'
import { removeWithOptionalConfirm } from 'view/retificacao-atendimento/removeWithOptionalConfirm'

import { showCodigoProcedimento } from '../../objetivo/resultados-exames/util-resultadosExames'
import { ALERTA_PROCEDIMENTO_INATIVO } from '../model-plano'
import { ProcedimentoPlanoModel } from './ProcedimentoPlanoField'

const path = metaPath<ProcedimentoPlanoModel>()

interface ProcedimentoPlanoRowProps {
  model: ProcedimentoPlanoModel
  onChange(values: ProcedimentoPlanoModel): void
  onRemove(itemToRemove: ProcedimentoPlanoModel): void
  onReset?(itemToReset: ProcedimentoPlanoModel): void
  status?: FieldStatusEnum
  controlModifiedStatus?: boolean
}

const validator = createValidator<ProcedimentoPlanoModel>({
  observacao: [maxLength(200)],
})

export function ProcedimentoPlanoRow(props: Readonly<ProcedimentoPlanoRowProps>) {
  const { model, onChange, onRemove, onReset = noop, status, controlModifiedStatus = false } = props
  const theme = useTheme()
  const styles = createStyles(theme)

  const [editing, setEditing] = useState(false)

  const { isEditable, tooltipText: removeTooltipText } = getEditableItemRemoveTooltipText(model)

  const editTooltipText = model.observacao ? 'Editar observação' : 'Adicionar observação'

  const toggleEditing = () => setEditing((oldState) => !oldState)

  const handleReset = () => onReset(model)

  const handleRemove = () =>
    removeWithOptionalConfirm({
      shouldAskConfirmation: controlModifiedStatus,
      title: 'Deseja excluir o campo SIGTAP?',
      icon: { name: 'exclamationTriangleOutline', fill: 'alert' },
      onRemove: () => onRemove(model),
    })

  const handleSubmit = (value: ProcedimentoPlanoModel, formApi: PecFormApi<ProcedimentoPlanoModel>) => {
    onChange(value)
    setTimeout(formApi.reset)
    toggleEditing()
  }

  const renderForm = (formRenderProps: FormRenderProps<ProcedimentoPlanoModel>) => (
    <VFlow vSpacing={0.5} style={styles.formContainer}>
      <Heading level={4}>Adicionar observação</Heading>
      <HLabel title='SIGTAP:'>{model.procedimento.descricao}</HLabel>
      <ObservacaoFormGrid
        name={path.observacao}
        onCancel={toggleEditing}
        onSubmit={formRenderProps.handleSubmit}
        dataTestId='observacoesSigtapRowPlano'
      />
    </VFlow>
  )

  return (
    <EditableRow editing={editing} dataTestId='ProcedimentoPlanoRow.Plano'>
      {!editing && (
        <>
          <Grid style={styles.grid}>
            <Cell size={9} style={styles.descricao}>
              <TextStatus status={status}>
                {`${model.procedimento.descricao} - ${showCodigoProcedimento(model.procedimento.codigo)}`}
              </TextStatus>
              {model.automatico && <Tag>Adicionado automaticamente</Tag>}
              {model.procedimento.ativo === false && (
                <Tooltip text={ALERTA_PROCEDIMENTO_INATIVO}>
                  <Tag style={styles.tagInativo} icon='exclamationTriangleOutline'>
                    Procedimento inativo
                  </Tag>
                </Tooltip>
              )}
            </Cell>
            <Cell size={3} style={styles.actions}>
              {controlModifiedStatus && (
                <ListFieldStatus status={status} onReset={handleReset} onRemove={handleRemove}>
                  <DropdownItem onClick={toggleEditing}>
                    <HFlow alignItems='center' hSpacing={0.5}>
                      <Text variant='h4'>{editTooltipText}</Text>
                    </HFlow>
                  </DropdownItem>
                </ListFieldStatus>
              )}

              {!controlModifiedStatus && (
                <>
                  <Tooltip text={editTooltipText}>
                    <Button
                      size='small'
                      skin='ghost'
                      onClick={toggleEditing}
                      data-testid='adicionarObservacaoSigtapPlano'
                    >
                      <Icon icon='chatOutline' />
                    </Button>
                  </Tooltip>
                  <Tooltip text={removeTooltipText}>
                    <Button size='small' skin='ghost' onClick={handleRemove} disabled={isEditable}>
                      <Icon icon='trashOutline' />
                    </Button>
                  </Tooltip>
                </>
              )}
            </Cell>
          </Grid>
          <Observacao observacao={model.observacao} status={status} />
        </>
      )}
      {editing && (
        <Form<ProcedimentoPlanoModel>
          render={renderForm}
          initialValues={model}
          onSubmit={handleSubmit}
          validate={validator}
          initialValuesEqual={initialValuesEqual}
        />
      )}
    </EditableRow>
  )
}

const initialValuesEqual = (procedimento1: ProcedimentoPlanoModel, procedimento2: ProcedimentoPlanoModel) =>
  procedimento1._id === procedimento2._id

const createStyles = (theme: Theme) => ({
  formContainer: css`
    padding: 1rem 0;
  `,
  tagInativo: css`
    background-color: ${theme.pallete.surface.main};
    border: 1px solid ${theme.pallete.status.alert.main};
    color: ${theme.pallete.status.alert.main};
  `,
  grid: css`
    margin: 0;
  `,
  descricao: css`
    display: flex;
    align-items: center;
    gap: 0.5rem;
  `,
  actions: css`
    align-self: center;
    padding: 0.25rem 0;
    text-align: right;
  `,
})
